import type { Domain } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import { useCallback } from "react";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { isTemplateInScope } from "../../utils/publisherPortal.utils";

export interface TemplateAccessResult {
    hasAccess: boolean;
    reason?: string;
    inputSources?: string[];
    inputSourceValidation?: {
        required: string | null;
        available: string[];
        hasMatch: boolean;
    };
}

export interface UseTemplateAccessValidationReturn {
    validateTemplateAccess: (
        domain: Domain,
        templateId: string,
        advertiserId: string,
    ) => Promise<TemplateAccessResult>;
}

export const useTemplateAccessValidation =
    (): UseTemplateAccessValidationReturn => {
        const validateTemplateAccess = useCallback(
            async (
                domain: Domain,
                templateId: string,
                advertiserId: string,
            ): Promise<TemplateAccessResult> => {
                // Check if template is advertiser-scoped
                if (!domain.metadata?.advertiserIdScoped) {
                    return { hasAccess: true };
                }

                // Find the specific template
                const template = domain.templates?.find(
                    (t) => t.id === templateId,
                );
                if (!template) {
                    return {
                        hasAccess: false,
                        reason: "Template not found in domain",
                    };
                }

                // Get input sources for the selected advertiser
                const client = await getCdbApiClient();
                const { data: inputSources = [] } =
                    await client.getInputSourcesByAdvertiserId({
                        advertiserId,
                    });

                const templateAssociation = {
                    ...template,
                    language: template.language ?? "en",
                    domain: {
                        id: domain.id,
                        name: domain.name,
                        metadata: domain.metadata,
                        tags: domain.tags,
                        enabled: domain.enabled,
                        lastUpdatedUser: domain.lastUpdatedUser,
                        lastUpdatedTimestamp: domain.lastUpdatedTimestamp,
                        programIdentifier: domain.programIdentifier,
                    },
                };

                const hasAccess = isTemplateInScope(
                    templateAssociation,
                    inputSources,
                    undefined,
                    false,
                );

                if (!hasAccess) {
                    return {
                        hasAccess: false,
                        reason: "Selected account does not have access to this template",
                        inputSources,
                    };
                }

                return {
                    hasAccess: true,
                    inputSources,
                };
            },
            [],
        );

        return { validateTemplateAccess };
    };
